import React, {useCallback} from 'react';
import type {AppearanceOptions} from '@stripe-internal/embedded-theming';
import type {
  ConnectJsOptions,
  IConnectJsOptionsWithDefaultValues,
  IPlatformSpecifiedConnectJsOptions,
} from '../../connect/ConnectJsOptions';
import {defaultConnectJsOptions} from '../../connect/ConnectJsOptions';
import type {InheritedStyles} from '../../connect/theming';
import type {UpdateOptions} from '../../connect/ConnectJSInterface/InitAndUpdateOptionsTypes';

type IConnectJsOptionsContext = {
  values: IConnectJsOptionsWithDefaultValues;
  platformProvidedValues: IPlatformSpecifiedConnectJsOptions;
  updateConnectJSOptions: (options: UpdateOptions) => void;
};

/**
 * Maps appearance options - this includes:
 * 1. Using inherited styles
 */
export const mapAppearanceOptions = (
  appearance: AppearanceOptions,
  inheritedStyles: InheritedStyles | undefined,
): AppearanceOptions => {
  const {variables} = appearance;
  let mappedAppearanceVariables = {...variables};

  // 1. Using inherited styles
  if (inheritedStyles?.fontFamily) {
    mappedAppearanceVariables = {
      ...mappedAppearanceVariables,
      fontFamily:
        mappedAppearanceVariables.fontFamily ?? inheritedStyles.fontFamily,
    };
  }

  return {
    ...appearance,
    variables: mappedAppearanceVariables,
  };
};

export const ConnectJsOptionsContext =
  React.createContext<IConnectJsOptionsContext>({
    values: defaultConnectJsOptions,
    platformProvidedValues: {},
    updateConnectJSOptions: () => {},
  });
ConnectJsOptionsContext.displayName = 'ConnectJsOptionsContext';

export type Props = {
  connectJsOptions: ConnectJsOptions;
  inheritedStyles: InheritedStyles | undefined;
  updateConnectJSOptions: (options: UpdateOptions) => void;
};

export const ConnectJsOptionsProvider: React.FC<Props> = ({
  children,
  connectJsOptions,
  inheritedStyles,
  updateConnectJSOptions,
}) => {
  const calculateConnectJsOptions = useCallback(() => {
    const calculatedConnectJsOptions = connectJsOptions.getValues();
    const mappedAppearanceOptions = mapAppearanceOptions(
      calculatedConnectJsOptions.appearance,
      inheritedStyles,
    );
    const adjustedConnectJsOptions = {
      ...calculatedConnectJsOptions,
      appearance: mappedAppearanceOptions,
    };

    return adjustedConnectJsOptions;
  }, [connectJsOptions, inheritedStyles]);

  const [currentOptions, setCurrentOptions] =
    React.useState<IConnectJsOptionsWithDefaultValues>(
      calculateConnectJsOptions(),
    );

  const [currentPlatformProvidedOptions, setCurrentPlatformProvidedOptions] =
    React.useState<IPlatformSpecifiedConnectJsOptions>(
      connectJsOptions.getPlatformSpecifiedValues(),
    );

  // When the deferred auth promise completes, we set auth context in state/context
  React.useEffect(() => {
    const observer = () => {
      // While we calculate the adjusted ConnectJS options based on flags and other parameters, we leave the platform specified options as is
      setCurrentPlatformProvidedOptions(
        connectJsOptions.getPlatformSpecifiedValues(),
      );
    };
    connectJsOptions.registerObserver(observer);
    return () => {
      connectJsOptions.unregisterObserver(observer);
    };
  }, [calculateConnectJsOptions, connectJsOptions, inheritedStyles]);

  React.useEffect(() => {
    const calculatedConnectJsOptions = calculateConnectJsOptions();
    setCurrentOptions(calculatedConnectJsOptions);
  }, [calculateConnectJsOptions, currentPlatformProvidedOptions]);

  return (
    <ConnectJsOptionsContext.Provider
      value={{
        values: currentOptions,
        platformProvidedValues: currentPlatformProvidedOptions,
        updateConnectJSOptions,
      }}
    >
      {children}
    </ConnectJsOptionsContext.Provider>
  );
};
